import { db, type NcbiValidationState, type Settings } from "@db";
import { updateSettings } from "@db/settings";
import { validateNcbiCredentials } from "@fetch/genbank/validate";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAutoSave } from "@hooks/use-auto-save";
import { timeSince } from "@lib/dates";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@ui/field";
import { Input } from "@ui/input";
import { SaveIndicator } from "@ui/save-indicator";
import { useLiveQuery } from "dexie-react-hooks";
import {
  AlertCircleIcon,
  CheckCircleIcon,
  LoaderCircleIcon,
} from "lucide-react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

export const Route = createFileRoute("/settings/services")({
  component: ServicesSettingsComponent,
});

const ncbiSchema = z.object({
  ncbiUsername: z.string(),
  ncbiApiKey: z
    .string()
    .regex(/^[a-f0-9]{36}$/, "API key must be a 36-character hex string")
    .or(z.literal("")),
});

const ebiEmailSchema = z.string().email().or(z.literal(""));

type NcbiFormData = z.infer<typeof ncbiSchema>;

function ServicesSettingsComponent() {
  const settings = useLiveQuery(() => db.settings.get("settings"));

  if (!settings) {
    return null;
  }

  return (
    <div className="max-w-xl space-y-6 p-6">
      <NcbiCredentialsSection settings={settings} />
      <EbiEmailSection settings={settings} />
    </div>
  );
}

function NcbiCredentialsSection({ settings }: { settings: Settings }) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<NcbiFormData>({
    resolver: zodResolver(ncbiSchema),
    defaultValues: {
      ncbiUsername: settings.ncbiUsername,
      ncbiApiKey: settings.ncbiApiKey,
    },
  });

  const [isValidating, setIsValidating] = useState(false);
  const [editedSinceSave, setEditedSinceSave] = useState(false);

  function handleFieldChange() {
    setEditedSinceSave(true);
  }

  async function onSubmit(data: NcbiFormData) {
    await updateSettings({
      ncbiUsername: data.ncbiUsername,
      ncbiApiKey: data.ncbiApiKey,
    });

    setEditedSinceSave(false);
    setIsValidating(true);

    const result = await validateNcbiCredentials({
      apiKey: data.ncbiApiKey,
      email: data.ncbiUsername,
    });

    const ncbiValidation: NcbiValidationState = {
      status: result.status,
      timestamp: new Date(),
      ...(result.status !== "success" && { message: result.message }),
    };

    await updateSettings({ ncbiValidation });
    setIsValidating(false);
  }

  const ncbiApiKey = watch("ncbiApiKey");
  const ncbiUsername = watch("ncbiUsername");
  const ncbiFieldsEmpty = !ncbiUsername && !ncbiApiKey;
  const persistedValidation =
    !editedSinceSave && !isValidating ? settings.ncbiValidation : undefined;

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <FieldSet>
        <FieldLegend>NCBI</FieldLegend>
        <FieldDescription className="-mt-2">
          Credentials used for BLAST searches and fetching sequence data.
        </FieldDescription>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="ncbiUsername">Username</FieldLabel>
            <Input
              id="ncbiUsername"
              {...register("ncbiUsername", {
                onChange: handleFieldChange,
              })}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="ncbiApiKey">API Key</FieldLabel>
            <Input
              id="ncbiApiKey"
              autoComplete="off"
              {...register("ncbiApiKey", {
                onChange: handleFieldChange,
              })}
            />
            <FieldError errors={[errors.ncbiApiKey]} />
          </Field>
        </FieldGroup>
        <div className="flex items-center gap-3">
          <Button
            type="submit"
            size="sm"
            disabled={ncbiFieldsEmpty || isSubmitting || isValidating}
          >
            Save
          </Button>
          {isValidating && (
            <span className="flex items-center gap-1.5 text-muted-foreground text-sm">
              <LoaderCircleIcon className="size-4 animate-spin" />
              Validating...
            </span>
          )}
          {persistedValidation && (
            <NcbiValidationDisplay validation={persistedValidation} />
          )}
        </div>
      </FieldSet>
    </form>
  );
}

function NcbiValidationDisplay({
  validation,
}: {
  validation: NcbiValidationState;
}) {
  if (validation.status === "success") {
    return (
      <span className="flex items-center gap-1.5 text-emerald-600 text-sm">
        <CheckCircleIcon className="size-4" />
        Verified {timeSince(validation.timestamp)}
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1.5 text-destructive text-sm">
      <AlertCircleIcon className="size-4" />
      {validation.message}
    </span>
  );
}

function EbiEmailSection({ settings }: { settings: Settings }) {
  const [value, setValue] = useState(settings.ebiEmail);

  const save = useCallback(async (email: string) => {
    await updateSettings({ ebiEmail: email });
  }, []);

  const { onBlur, showSaved, error } = useAutoSave({
    save,
    schema: ebiEmailSchema,
  });

  return (
    <FieldSet>
      <FieldLegend className="flex items-center gap-2">
        EBI
        <SaveIndicator visible={showSaved} />
      </FieldLegend>
      <FieldDescription className="-mt-2">
        Email address used for InterProScan job submissions.
      </FieldDescription>
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="ebiEmail">Email</FieldLabel>
          <Input
            id="ebiEmail"
            type="email"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => onBlur(value)}
          />
          {error && <FieldError errors={[{ message: error }]} />}
        </Field>
      </FieldGroup>
    </FieldSet>
  );
}
